import { SUBSCRIPTION_COMPONENT_TRANSLATIONS as T } from "@/components/subscription/constants";
import type { ClinicSetupPayload } from "@/domains/multi/clinicTypes";
import { ClinicTaxIdentityFields, validateClinicTaxIdentityForm } from "@/domains/multi/ClinicTaxIdentityFields";
import { ClinicTaxTypes } from "@/domains/multi/clinicRoles";
import type { ClinicTaxType } from "@/domains/multi/clinicTypes";
import { Button } from "@/domains/shared/components/Button";
import { getTranslation } from "@mivo/i18n";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export type ClinicSetupStepProps = {
    userName: string;
    userEmail: string;
    userCpf?: string | null;
    userPhone?: string | null;
    loading: boolean;
    onBack: () => void;
    onSubmit: (payload: ClinicSetupPayload) => Promise<void>;
};

export default function ClinicSetupStep({ userName, userEmail, userCpf, userPhone, loading, onBack, onSubmit }: ClinicSetupStepProps) {
    const cpfLocked = Boolean(userCpf?.replace(/\D/g, ""));
    const [clinicTaxType, setClinicTaxType] = useState<ClinicTaxType | "">(ClinicTaxTypes.Cpf);
    const [clinicName, setClinicName] = useState(userName);
    const [clinicCpfCnpj, setClinicCpfCnpj] = useState((userCpf ?? "").replace(/\D/g, ""));
    const [clinicPhone, setClinicPhone] = useState(userPhone ?? "");

    useEffect(() => {
        if (clinicTaxType === ClinicTaxTypes.Cpf) {
            setClinicName(userName);
            if (cpfLocked) {
                setClinicCpfCnpj((userCpf ?? "").replace(/\D/g, ""));
            }
        }
    }, [clinicTaxType, userName, userCpf, cpfLocked]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationError = validateClinicTaxIdentityForm(clinicTaxType, clinicCpfCnpj, clinicName, clinicPhone);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        await onSubmit({
            clinicName: clinicName.trim(),
            clinicTaxType: clinicTaxType as ClinicTaxType,
            clinicCpfCnpj,
            clinicPhone: clinicPhone.trim() || undefined,
        });
    };

    return (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" data-testid="clinic-setup-step">
            <p className="text-sm text-muted-foreground">{getTranslation(T.clinicSetupIntro)}</p>

            <ClinicTaxIdentityFields
                clinicTaxType={clinicTaxType}
                clinicName={clinicName}
                clinicCpfCnpj={clinicCpfCnpj}
                clinicPhone={clinicPhone}
                userCpf={userCpf}
                userName={userName}
                cpfLocked={cpfLocked}
                readOnlyEmail={userEmail}
                idPrefix="clinic-setup"
                onTaxTypeChange={setClinicTaxType}
                onClinicNameChange={setClinicName}
                onCpfCnpjChange={setClinicCpfCnpj}
                onPhoneChange={setClinicPhone}
            />

            <div className="flex flex-row gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={onBack} disabled={loading}>
                    {getTranslation(T.back)}
                </Button>
                <Button type="submit" className="flex-1" disabled={loading} data-testid="clinic-setup-submit">
                    {loading ? getTranslation(T.saving) : getTranslation(T.continue)}
                </Button>
            </div>
        </form>
    );
}
